/**
 * Input normalization — accept chart data with either camelCase keys (the
 * public TypeScript contract) or PascalCase keys (e.g. JSON straight from a
 * .NET API: `Categories`, `Series`, `Name`, `Values`, `Label`, `Value`,
 * `Color`).
 *
 * The framework wrappers run these helpers at their data boundary, so every
 * downstream consumer — geometry, a11y labels and the visually-hidden a11y
 * data-table templates — only ever sees canonical camelCase. This is the
 * single place that checks both spellings of each key.
 */
import type { XaCategorical, XaSeries, XaSlice } from './types.js';

/** Read `obj[key]` (camelCase), falling back to its PascalCase spelling (`Key`). */
function pick(obj: Record<string, unknown>, key: string): unknown {
  const upper = key.charAt(0).toUpperCase() + key.slice(1);
  const lower = obj[key];
  return lower !== undefined ? lower : obj[upper];
}

/** Normalize one categorical series (`{ name, values, color? }`). */
export function normalizeSeries(input: unknown): XaSeries {
  const s = (input ?? {}) as Record<string, unknown>;
  const color = pick(s, 'color');
  return {
    name: (pick(s, 'name') ?? '') as string,
    values: (pick(s, 'values') ?? []) as number[],
    ...(color !== undefined ? { color: color as string } : {}),
  };
}

/** Normalize categorical chart data (`{ categories, series }`): stacked bar, line, area. */
export function normalizeCategorical(input: unknown): XaCategorical {
  const d = (input ?? {}) as Record<string, unknown>;
  const series = pick(d, 'series');
  return {
    categories: (pick(d, 'categories') ?? []) as string[],
    series: Array.isArray(series) ? series.map(normalizeSeries) : [],
  };
}

/** Normalize one slice (`{ label, value, color? }`). */
export function normalizeSlice(input: unknown): XaSlice {
  const d = (input ?? {}) as Record<string, unknown>;
  const color = pick(d, 'color');
  return {
    label: (pick(d, 'label') ?? '') as string,
    value: (pick(d, 'value') ?? 0) as number,
    ...(color !== undefined ? { color: color as string } : {}),
  };
}

/** Normalize slice chart data (donut / pie): an array of slices. */
export function normalizeSlices(input: unknown): XaSlice[] {
  return Array.isArray(input) ? input.map(normalizeSlice) : [];
}
